import { Box, Typography } from '@mui/material';
import { TERA_TYPE_OPTIONS_WITHOUT_NONE } from '../../constants/tera-types';
import { AttackPokemonData } from '../../types/attack';
import TeraTypeSelect from '../TeraTypeSelect';

interface AttackPokemonTeraSelectorProps {
  variant: 'simple' | 'stateful';
  attackPokemon: AttackPokemonData;
  handleSelectTerasuTypeForAttackPokemon: (id: string, terasuType: number) => void;
  handleSelectTerasuStateForAttackPokemon?: (id: string, terasuState: 'none' | 'type' | 'stellar') => void;
}

function AttackPokemonTeraSelector({
  variant,
  attackPokemon,
  handleSelectTerasuTypeForAttackPokemon,
  handleSelectTerasuStateForAttackPokemon,
}: AttackPokemonTeraSelectorProps) {
  const isTerapagosStella = attackPokemon.pokemon.id === 10277;
  if (variant === 'stateful' && !attackPokemon.selectedMove) {
    return null;
  }

  if (isTerapagosStella) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
        <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
          テラスタイプ
        </Typography>
        <Box
          sx={{
            minWidth: variant === 'simple' ? 80 : 40,
            height: variant === 'simple' ? 24 : 20,
            border: '1px solid #FFD700',
            borderRadius: 0.5,
            backgroundColor: '#FFD700',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '0.7rem',
            fontWeight: 'bold',
            color: 'black',
          }}
        >
          ステラ
        </Box>
      </Box>
    );
  }

  if (variant === 'simple') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
        <TeraTypeSelect
          value={attackPokemon.terasuType}
          onChange={(value) => handleSelectTerasuTypeForAttackPokemon(attackPokemon.id, value)}
          variant="sidebar"
          label="テラスタイプを選択"
          stopPropagation
        />
      </Box>
    );
  }

  const handleToggleState = (state: 'type' | 'stellar') => {
    if (!handleSelectTerasuStateForAttackPokemon) return;
    const currentState = attackPokemon.terasuState || 'none';
    const nextState = currentState === state ? 'none' : state;
    handleSelectTerasuStateForAttackPokemon(attackPokemon.id, nextState);
  };

  const teraBlastMoveId = 851;
  const isTeraBlast = attackPokemon.selectedMove?.id === teraBlastMoveId;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
      {isTeraBlast ? (
        <TeraTypeSelect
          value={attackPokemon.terasuType || attackPokemon.pokemon.often_used_terasu?.[0] || 1}
          onChange={(value) => handleSelectTerasuTypeForAttackPokemon(attackPokemon.id, value)}
          variant="sidebar"
          label="テラスタイプを選択"
          stopPropagation
          includeNoneOption={false}
          options={(() => {
            const top10Types = attackPokemon.pokemon.often_used_terasu?.slice(0, 10) || [];
            const stellarTypeId = 19;
            const displayTypes = top10Types.includes(stellarTypeId) ? top10Types : [...top10Types, stellarTypeId];
            const uniqueTypes = Array.from(new Set(displayTypes));
            return uniqueTypes
              .map((typeId) => TERA_TYPE_OPTIONS_WITHOUT_NONE.find((typeOption) => typeOption.id === typeId))
              .filter((typeOption): typeOption is typeof TERA_TYPE_OPTIONS_WITHOUT_NONE[number] => Boolean(typeOption));
          })()}
        />
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
          <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
            テラス
          </Typography>
          <Box
            onClick={(e) => {
              e.stopPropagation();
              handleToggleState('type');
            }}
            sx={{
              minWidth: 40,
              height: 20,
              border: '1px solid #555',
              borderRadius: 0.5,
              backgroundColor: attackPokemon.terasuState === 'type' ? '#4CAF50' : 'transparent',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.65rem',
              fontWeight: 'bold',
              color: attackPokemon.terasuState === 'type' ? 'white' : '#555',
              '&:hover': {
                backgroundColor: attackPokemon.terasuState === 'type' ? '#45a049' : '#f5f5f5',
              },
            }}
          >
            テラス
          </Box>
          <Box
            onClick={(e) => {
              e.stopPropagation();
              handleToggleState('stellar');
            }}
            sx={{
              minWidth: 40,
              height: 20,
              border: '1px solid #555',
              borderRadius: 0.5,
              backgroundColor: attackPokemon.terasuState === 'stellar' ? '#FFD700' : 'transparent',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.65rem',
              fontWeight: 'bold',
              color: attackPokemon.terasuState === 'stellar' ? 'black' : '#555',
              '&:hover': {
                backgroundColor: attackPokemon.terasuState === 'stellar' ? '#e6c200' : '#f5f5f5',
              },
            }}
          >
            ステラ
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default AttackPokemonTeraSelector;
